import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function YorkCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="York Pothole Claims | 25% of Residential Roads in RED | Fixtyer"
        description="City of York Council repaired 9,074 potholes in 2024/25 and spends nearly triple its DfT allocation — yet 25% of unclassified roads are in RED condition, worse than neighbouring councils. See what the AMBER DfT rating means for your claim."
        canonical="/councils/york"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'York', url: '/councils/york' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'York spends nearly triple its DfT capital allocation — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but your claim turns on the specific defect and road condition — and York is AMBER overall because unclassified roads are deteriorating. Section 58 depends on whether the pothole that damaged your vehicle was reasonably inspected and repaired, not on aggregate spend figures.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 551km — 70% of York\'s 790km road network. City of York Council\'s own transparency report shows 25% of U-roads in RED condition in 2024/25, up from 11% in 2019/20, and admits U-road condition is worse than neighbouring councils. Most pothole damage in York happens on these local streets.',
          },
          {
            question: 'Does York\'s claim of 100% annual survey coverage weaken my claim?',
            answer: 'Not automatically. The council uses AI video surveys from bicycle-mounted smartphones and says it achieves full-network coverage each year. That can support a Section 58 defence — but only if the survey actually recorded your road and the defect was visible at the time. Prior reports, photos showing defect age, and whether your street was on the annual programme matter more than the headline coverage claim.',
          },
          {
            question: 'Why does the report show 2,255 job tickets but 9,074 pothole repairs?',
            answer: 'City of York Council explains that job tickets can cover multiple repair areas and defect types. From April 2024, its new Asset Management System tracks individual potholes separately — revealing 9,074 individual repairs in 2024/25, roughly four times the job-ticket count. The true scale of reactive work is larger than the older headline figures suggest.',
          },
          {
            question: 'Does York\'s drainage investment help or hurt my claim?',
            answer: 'Both, depending on your road. The council targets £1,000k on drainage in 2025/26 and acknowledges standing water turns small defects into potholes. If your road has known flooding issues the council has prioritised but not fixed, that supports a claim. If drainage was recently improved and your pothole formed afterwards, the council may argue it took reasonable steps.',
          },
          {
            question: 'Do streetworks and York Central development affect claim prospects?',
            answer: 'Potentially. The council reports 12,369 streetworks permit applications in 2024/25, 27 new development sites including Station gateway and York Central, and notes growth "inevitably" brings roadworks that create pressures where new and old infrastructure meet. Heavy utility and construction traffic accelerates wear — relevant if your incident was on a corridor affected by permitted works.',
          },
          {
            question: 'Is pre-2019/20 condition data comparable?',
            answer: 'City of York Council warns that data between 2019 and 2023 for A and B/C roads, and between 2019 and 2022 for U-roads, was collected using survey types that do not produce outputs comparable with current DfT national indicators. For claims, focus on the latest comparable figures — especially the 25% RED U-road rate in 2024/25.',
          },
        ]}
      />
      <ServiceSchema
        councilName="City of York Council"
        councilSlug="york"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/york"
        headline="York Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">York</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            York: one in four local roads needs maintenance
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            City of York Council spends nearly triple its DfT capital allocation and earns a GREEN spend
            scorecard. Yet the overall DfT rating is <strong className="text-amber-400">AMBER</strong> because
            <strong className="text-rose-400"> 25% of unclassified roads are in RED condition</strong> — more
            than double the 11% recorded in 2019/20, and worse than neighbouring councils. On a 790km network
            where 70% of roads are residential U-roads, that is where most pothole damage happens.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">9,074</div>
            <div className="text-white font-medium mt-2">Individual potholes repaired in 2024/25</div>
            <div className="text-brand-300 text-sm mt-1">
              New Asset Management System data — roughly four times the 2,255 job-ticket figure the council
              previously published, revealing the true scale of reactive repairs across York.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from City of York Council&apos;s own Highway Condition Report 2025 —
            classified roads holding up, local streets slipping
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (75km — 9.5% of network): stable</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">3%</div>
                <div className="text-sm text-rose-800">RED (2024/25)</div>
                <div className="text-xs text-surface-500">down from 8% in 2019/20</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">21%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">up from 52% in 2019/20*</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">76%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">up from 40% in 2019/20*</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              York&apos;s A-roads are in good condition with only 2–3% rated as needing maintenance consideration
              on the latest SCANNER surveys — aligned with regional and England averages. But A-roads are
              less than one-tenth of the network.
            </p>
            <p className="text-surface-500 text-xs mt-2 italic">
              *Council footnote: 2019–2023 A-road data used survey types not comparable with current DfT national indicators.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (164km — 21% of network): broadly stable</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2019/20</td>
                    <td className="px-4 py-2 text-center">16%</td>
                    <td className="px-4 py-2 text-center">59%</td>
                    <td className="px-4 py-2 text-center">25%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">18%</td>
                    <td className="px-4 py-2 text-center">62%</td>
                    <td className="px-4 py-2 text-center">20%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">21%</td>
                    <td className="px-4 py-2 text-center">75%</td>
                  </tr>
                  <tr className="bg-brand-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">4%</td>
                    <td className="px-4 py-2 text-center font-bold">23%</td>
                    <td className="px-4 py-2 text-center font-bold">73%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B and C roads show only 4% in RED condition — better than the England average of 7%. The council
              surveys these roads annually and uses the data to develop Highways Annual Maintenance Programmes.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">Well funded — but local roads still declining</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£4.4m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£12.2m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£5.1m</div>
                <div className="text-brand-300 text-sm">Additional council highways budget approved</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              City of York Council projects spending nearly <span className="text-emerald-400">triple its DfT allocation</span> in
              2025/26, with Members approving an extra £5,070k for highways on top. The spend scorecard is GREEN —
              yet U-road RED condition has risen to 25%. Investment is not keeping pace with deterioration on the
              roads where most residents actually drive.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road problem */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 551km local road problem
          </h2>
          <p className="text-surface-600 mb-8">
            70% of York&apos;s network is unclassified roads — and condition is worse than neighbouring councils
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2019/20</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">11%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">12%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">20%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">16%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">19%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600 text-lg">25%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Worse than the neighbours</h3>
              <p className="text-surface-600 text-sm mb-3">
                City of York Council states plainly that U-road condition &ldquo;isn&apos;t as good as our
                neighbouring councils&rdquo; and at 19% in 2023/24 was slightly above both the regional and
                England average of 17%. The 2024/25 figure has since risen to 25%.
              </p>
              <p className="text-surface-600 text-sm">
                On 551km of U-roads, 25% RED means roughly <strong>138km of local streets</strong> should be
                considered for maintenance — suburban roads, estate streets, village lanes and city-centre side roads.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The council&apos;s own response</h3>
              <p className="text-amber-800 text-sm mb-3">
                York is increasing preventative surface treatments across the network with a focus on including
                more U-roads on each Annual Highway Maintenance Programme. Surface dressing aims to seal roads
                before cracks become potholes — treatments the council says can last 8 to 10 years when sites
                are selected effectively.
              </p>
              <p className="text-amber-800 text-sm">
                The question for claimants is whether your road was on that programme — or left in the 25% RED
                category while awaiting treatment.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, City of York Council must show it had a reasonable system for
              knowing the condition of its roads. For York&apos;s unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your residential street in the 25% RED category at the last survey?</li>
              <li>• If so, was it scheduled for surface dressing, resurfacing or reactive repair?</li>
              <li>• Did the council&apos;s AI bicycle survey record the defect before your incident?</li>
              <li>• With two thirds of revenue spend going to reactive pothole repairs, why was yours missed?</li>
            </ul>
            <p className="text-white font-medium">
              A council that publishes 25% RED on local roads cannot claim ignorance of widespread deterioration
              on the road type that makes up 70% of its network.
            </p>
          </div>
        </div>
      </section>

      {/* Reactive repair scale */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            14,096 job tickets — and 9,074 individual repairs
          </h2>
          <p className="text-surface-600 mb-8">
            Two ways of counting the same reactive maintenance conveyor belt
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Pothole job tickets</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">3,392</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">3,003</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">3,168</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">2,278</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">2,255</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">14,096</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">The AMS revelation</h3>
              <p className="text-rose-800 text-sm mb-3">
                From April 2024, York&apos;s new Asset Management System tracks individual potholes separately.
                In 2024/25 alone, it recorded <strong>9,074 individual pothole repairs</strong> — roughly four
                times the 2,255 job-ticket figure.
              </p>
              <p className="text-rose-800 text-sm">
                Job tickets can include multiple repair areas and defect types. The true volume of reactive
                pothole work is substantially higher than the headline job-ticket table suggests.
              </p>
            </div>

            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Two thirds of revenue on reactive repairs</h3>
              <p className="text-surface-600 text-sm">
                City of York Council spends approximately two thirds of its revenue basic maintenance budget on
                reactive pothole and masonry repairs each year. The council itself describes reactive maintenance
                as &ldquo;generally considered to be an inefficient use of resources&rdquo; — yet defects
                &ldquo;will inevitably form&rdquo; and &ldquo;very often develop very quickly requiring a reactive
                response.&rdquo;
              </p>
            </div>
          </div>

          <QuoteCard
            quote="Defects will inevitably form on parts of the highway network at some point in time and very often these defects will develop very quickly requiring a reactive response."
            source="City of York Council — Highway Condition Report 2025"
          />
        </div>
      </section>

      {/* Growth and streetworks */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Growth, streetworks and network pressure
          </h2>
          <p className="text-surface-600 mb-8">
            York is a growing city — and the council documents what that does to its roads
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="York is a vibrant, prosperous and growing city that continues to attract new residents and businesses. However, with growth inevitably come roadworks with the demands on our highway network keeping pace as utilities seek to upgrade existing or install additional assets to serve new infrastructure, often along key busy transport routes."
              source="City of York Council — Highway Condition Report 2025"
            />
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Streetworks metric (2024/25)</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Figure</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Permit applications submitted</td>
                  <td className="px-6 py-4 text-center font-bold">12,369</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Permit applications granted</td>
                  <td className="px-6 py-4 text-center font-bold">7,787</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Occupancy days on the network</td>
                  <td className="px-6 py-4 text-center font-bold">11,510</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">New development sites (incl. Station gateway and York Central)</td>
                  <td className="px-6 py-4 text-center font-bold">27</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">Major schemes</td>
                  <td className="px-6 py-4 text-center font-bold">368</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What this admission means</h3>
              <p className="text-surface-600 text-sm mb-3">
                The council acknowledges that construction of new infrastructure creates pressures where
                &ldquo;new and old meet&rdquo; — often requiring significant changes to existing road layouts
                with associated disruption. That is documented knowledge of elevated wear on specific corridors.
              </p>
              <p className="text-surface-600 text-sm">
                If your pothole was on a route affected by York Central, Station gateway or utility streetworks,
                the council knew the network was under above-normal stress.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Drainage and climate</h3>
              <p className="text-amber-800 text-sm mb-3">
                York targets £1,000k on drainage in 2025/26 and notes that standing water combined with freeze-thaw
                and traffic action &ldquo;can readily turn small defects in road surfaces into potholes.&rdquo;
              </p>
              <p className="text-amber-800 text-sm">
                The council is already seeing climate impacts — melting road surfaces and subsidence — on its
                highways network. Known flooding issues the council has prioritised but not yet resolved are
                relevant to claim evidence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Survey methodology */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            100% survey coverage — what it actually means
          </h2>
          <p className="text-surface-600 mb-8">
            City of York Council&apos;s AI bicycle surveys and the AMBER best-practice rating
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend — projects nearly triple DfT capital allocation in 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Highway Infrastructure Asset Management Plan published 2021, aligned with UKRLG guidance
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> AI video surveys claim 100% annual network coverage via bicycle-mounted smartphones
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A and B/C roads in comparatively good condition (3–4% RED)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Defect hot-spot clustering used to prioritise programmes
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence on classified roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — 25% of U-roads in RED, worse than neighbouring councils
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-road RED rate more than doubled since 2019/20 (11% → 25%)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 9,074 individual pothole repairs in 2024/25 — defects forming faster than prevention catches them
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Only 27% of projected 2025/26 spend on preventative maintenance
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice — not the top tier of DfT asset-management ratings
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Documented growth and streetworks pressure on the network
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and a published asset management plan, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, York&apos;s online reporting) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, cite the 25% RED rate and whether your street was on the maintenance programme</li>
              <li>• Location relative to York Central, Station gateway or active streetworks permits</li>
              <li>• Whether known drainage issues on your road were on the council&apos;s priority list</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites City of York Council&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole to York first
          </h2>
          <p className="text-surface-600 mb-6">
            City of York Council aims to respond to non-urgent road reports within four working days and urgent
            reports within two hours. Reporting the defect through the council creates a dated record — useful
            evidence if the pothole was reported before your incident, or if the council failed to assess and
            repair it within a reasonable period.
          </p>
          <a
            href="https://www.york.gov.uk/ReportRoadsPavementsPotholes"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a pothole to City of York Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            Verify the road is adopted using York&apos;s online map before reporting. Keep your reference number
            and any confirmation emails. Out-of-hours emergencies: 01904 551550.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in York?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded AMBER council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road deterioration argument</li>
                <li>• No prior-report search</li>
                <li>• No streetworks corridor analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 25% U-road RED rate documented</li>
                <li>✅ 9,074 pothole repairs in 2024/25 cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Drainage and streetworks context included</li>
                <li>✅ Section 58 rebuttal tailored to York</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=york"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my York claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                York spends nearly triple its DfT capital allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but your claim turns on the specific defect and road
                condition — and York is AMBER overall because unclassified roads are deteriorating. Section 58
                depends on whether the pothole that damaged your vehicle was reasonably inspected and repaired,
                not on aggregate spend figures.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 551km — 70% of York&apos;s 790km road network. City of York Council&apos;s own
                transparency report shows 25% of U-roads in RED condition in 2024/25, up from 11% in 2019/20,
                and admits U-road condition is worse than neighbouring councils. Most pothole damage in York
                happens on these local streets.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does York&apos;s claim of 100% annual survey coverage weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Not automatically. The council uses AI video surveys from bicycle-mounted smartphones and says
                it achieves full-network coverage each year. That can support a Section 58 defence — but only if
                the survey actually recorded your road and the defect was visible at the time. Prior reports,
                photos showing defect age, and whether your street was on the annual programme matter more than
                the headline coverage claim.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why does the report show 2,255 job tickets but 9,074 pothole repairs?
              </h3>
              <p className="text-surface-600 text-sm">
                City of York Council explains that job tickets can cover multiple repair areas and defect types.
                From April 2024, its new Asset Management System tracks individual potholes separately — revealing
                9,074 individual repairs in 2024/25, roughly four times the job-ticket count. The true scale of
                reactive work is larger than the older headline figures suggest.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does York&apos;s drainage investment help or hurt my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Both, depending on your road. The council targets £1,000k on drainage in 2025/26 and acknowledges
                standing water turns small defects into potholes. If your road has known flooding issues the
                council has prioritised but not fixed, that supports a claim. If drainage was recently improved
                and your pothole formed afterwards, the council may argue it took reasonable steps.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Do streetworks and York Central development affect claim prospects?
              </h3>
              <p className="text-surface-600 text-sm">
                Potentially. The council reports 12,369 streetworks permit applications in 2024/25, 27 new
                development sites including Station gateway and York Central, and notes growth &ldquo;inevitably&rdquo;
                brings roadworks that create pressures where new and old infrastructure meet. Heavy utility and
                construction traffic accelerates wear — relevant if your incident was on a corridor affected by
                permitted works.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Is pre-2019/20 condition data comparable?
              </h3>
              <p className="text-surface-600 text-sm">
                City of York Council warns that data between 2019 and 2023 for A and B/C roads, and between 2019
                and 2022 for U-roads, was collected using survey types that do not produce outputs comparable with
                current DfT national indicators. For claims, focus on the latest comparable figures — especially
                the 25% RED U-road rate in 2024/25.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.york.gov.uk/streets-roads-pavements/highway-condition-report-2025"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              City of York Council — Highway Condition Report 2025
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
